import { LeftCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import { Button, Grid, Layout, Space, Tag, Tooltip, Typography } from "antd";
import React, { useEffect, useMemo } from "react";
import ReactGA from "react-ga4";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { appVersion, now } from "../../helpers/format";
import useAuth from "../../hooks/useAuth";
import useConfigApp from "../../hooks/useConfigApp";
import useMediaQuery from "../../hooks/useMediaQuery";
import { EMPLOYEE_ROLES_MAPPING } from "../../types/employee.type";
import AppLogo from "../AppLogo";
import AppNotification from "../AppNotification";
import AppVersion from "../AppVersion";
import Breadcrumbs from "../Breadcrumbs";
import UserAvatar from "../UserAvatar";
import AppMenuSidebar from "../sidebar/SidebarMenu";

if (process.env.REACT_APP_GA_ID) ReactGA.initialize(process.env.REACT_APP_GA_ID);

const { Header, Sider, Content } = Layout;

const AppLayout: React.FC = ({ children }) => {
	const history = useHistory();
	const { md } = Grid.useBreakpoint();
	const { sidebarCollapsed, toggleSidebar } = useConfigApp();
	const location = useLocation();
	const isBelowLg = useMediaQuery("(max-width: 991px)");
	const { roles, getThemeRole, roleColor } = useAuth();
	useEffect(() => {
		if (isBelowLg && !sidebarCollapsed) {
			toggleSidebar();
		}

		// eslint-disable-next-line
	}, [location, isBelowLg]);

	const isFakeCollapsed = useMemo(() => {
		if (!isBelowLg) {
			return sidebarCollapsed;
		}
		return false;
	}, [isBelowLg, sidebarCollapsed]);

	const excludeRoles = useMemo(() => {
		const uniqueRoles = Array.from(new Set(roles));
		return uniqueRoles.filter((v) => v !== getThemeRole.role);
	}, [getThemeRole, roles]);

	const hideRole = useMemo(() => {
		const pathSegments = location.pathname.split("/").filter(Boolean);
		const detailRoutes = ["hq", "franchise-owner", "staff", "agent", "users"];

		if (pathSegments.length >= 2) {
			const route = pathSegments[0];
			const id = pathSegments[1];
			return detailRoutes.includes(route) && id.length > 15;
		}

		return false;
	}, [location.pathname]);

	return (
		<Layout style={{ height: "100%", flexDirection: "row" }}>
			<AppSider
				// onCollapse={toggleSidebar}
				trigger={null}
				collapsible
				collapsed={!isFakeCollapsed}
				width={250}
				style={{ height: "100%" }}
				breakpoint="lg"
				className={sidebarCollapsed ? "real-collapsed" : ""}
			>
				<AppLogo
					collapsed={!isFakeCollapsed}
					onCollapse={toggleSidebar}
					onClick={() => {
						if (location.pathname === "/dashboard" || location.pathname === "/") {
							const scrollContent = document.getElementById("scroll-content");
							if (scrollContent) {
								scrollContent.scrollTo({ top: 0, behavior: "smooth" });
							}
						} else {
							history.push("/dashboard");
						}
					}}
				/>
				<AppMenuSidebar />

				<FootSidebarContainer>
					{!isFakeCollapsed ? (
						<Tooltip
							placement="right"
							title={sidebarCollapsed && !isBelowLg ? "Expand Sidebar" : undefined}
						>
							<Space
								direction="vertical"
								size={0}
							>
								<MaximizeButton
									onClick={toggleSidebar}
									type="text"
									icon={
										<RightCircleOutlined
											color="#FFFFFF"
											className="icon-collapsed"
											style={{ fontSize: 24 }}
										/>
									}
								/>
								<Typography.Text
									style={{
										fontSize: 8,
										color: "#FFFFFF",
										textAlign: "center",
										display: "block",
									}}
								>
									v{appVersion}
								</Typography.Text>
							</Space>
						</Tooltip>
					) : (
						<MinimizeButton onClick={toggleSidebar}>
							<AppVersion />
							<LeftCircleOutlined
								color="#FFFFFF"
								style={{ fontSize: 24 }}
							/>
						</MinimizeButton>
					)}
				</FootSidebarContainer>
			</AppSider>

			<Layout
				id="scroll-content"
				className="site-layout"
			>
				{/* <div style={{ height: 4, backgroundColor: roleColorUser }}></div> */}
				<AppHeaderWrapper>
					<AppHeaderNew bgColor={getThemeRole.theme}>
						<Space size={12}>
							<img src="/icons/dashboard.svg" />

							<Breadcrumbs />

							{!hideRole && (
								<>
									<div
										style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#fff" }}
									></div>
									<Typography.Text
										strong
										style={{
											fontSize: 16,
											color: "#fff",
										}}
									>
										{getThemeRole.roleName}
									</Typography.Text>
								</>
							)}
						</Space>
						<AppHeaderAccount>
							<div
								style={{
									lineHeight: 1,
									gap: 4,
									display: md ? "flex" : "none",
									flexDirection: "column",
									alignItems: "flex-end",
									marginRight: 10,
								}}
							>
								<DateNow className="show-md">{now}</DateNow>
								<Space
									size={md ? 0 : 2}
									wrap
								>
									<AppTag color="default">{getThemeRole.roleName}</AppTag>
									{excludeRoles.length > 0 && (
										<Tooltip
											title={
												<Space
													size={md ? 0 : 2}
													wrap
												>
													{excludeRoles.map((role) => (
														<AppTag color={roleColor(role)}>
															{EMPLOYEE_ROLES_MAPPING[role]}
														</AppTag>
													))}
												</Space>
											}
										>
											<div
												style={{
													color: "#262626",
													backgroundColor: "white",
													fontSize: 11,
													fontWeight: "bold",
													padding: "4px 8px",
													display: "grid",
													placeItems: "center",
													borderRadius: 20,
													border: `1px solid ${
														getThemeRole.theme === "default"
															? "#262626"
															: getThemeRole.theme
													}`,
												}}
											>
												{excludeRoles.length}+
											</div>
										</Tooltip>
									)}
								</Space>
							</div>
							<AppNotification />
							<DividerTitle className="show-md" />
							<UserAvatar />
						</AppHeaderAccount>
					</AppHeaderNew>
				</AppHeaderWrapper>
				<AppContent>
					{children}
					<BottomSpace />
				</AppContent>
			</Layout>
		</Layout>
	);
};

const AppContent = styled(Content)`
	height: 100%;
	padding: 20px 10px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	@media screen and (min-width: 768px) {
		padding: 20px;
	}
`;

const AppHeaderNew = styled.header<{ bgColor: string }>`
	padding: 12px 14px;
	background-color: ${(props) => props.bgColor};
	border-radius: 9999px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	box-shadow: rgb(255 255 255 / 35%) 0px 5px 15px;
`;

const AppHeaderWrapper = styled.div`
	padding: 20px 10px 0px;
	position: sticky;
	top: 0;
	z-index: 999;
	background-color: #e8ecf3;
	@media screen and (min-width: 768px) {
		padding: 20px 20px 0px;
	}
`;

export const AppTag = styled(Tag)`
	font-size: 11px;
	padding: 0px 10px;
	line-height: 18px;
`;

const AppSider = styled(Sider)`
	background-color: ${({ theme }) => theme.colors.primary};
	overflow: auto;
	overflow-x: hidden;
	padding-left: 10px;
`;

const AppHeader = styled(Header)`
	position: sticky;
	top: 0;
	z-index: 999;
	padding: 0 10px;
	height: 64px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	@media screen and (min-width: 992px) {
		padding: 20px;
		position: relative;
		padding: 0 20px;
	}
`;

const DateNow = styled.div`
	color: ${({ theme }) => theme.colors.white};
	text-align: right;
	line-height: inherit;
`;

const AppHeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;
const AppHeaderAccount = styled.div`
	flex: 1;
	display: grid;
	gap: 20px;
	grid-template-columns: 1fr auto auto auto;
	align-items: center;
	justify-content: flex-end;
	text-align: right;
`;

const DividerTitle = styled.div`
	width: 1px;
	height: 40%;
	background-color: ${({ theme }) => theme.colors.ash800};
	opacity: 0.4;
	margin: 0 14px;
`;
const FootSidebarContainer = styled.div`
	position: fixed;
	bottom: 0;
	height: 70px;
	padding-bottom: 10px;
	background: #00133b;
`;
const MinimizeButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 16px;
	cursor: pointer;
	width: 240px;
	z-index: 1001;
	height: 100%;

	div {
		color: ${({ theme }) => theme.colors.white};
		font-size: 10px;
	}

	svg {
		fill: ${({ theme }) => theme.colors.white};
	}
`;

const MaximizeButton = styled(Button)`
	padding: 10px 16px;
	height: 100%;
	width: 70px;
	z-index: 1001;
	@media (max-width: 991px) {
		display: none;
	}
`;

const BottomSpace = styled.div`
	height: 40px;
`;

export default AppLayout;
